
import NumAbbr from "number-abbreviate";

/**
 * Formats a number into the app's abbreviated notation (K / M / Bn / Tr).
 * Mirrors the mobile app implementation so web and app show identical values.
 *
 * @param {string|number} value Raw value to format.
 * @param {number} [decimals=2] Pass 0 to disable decimals, any other value keeps up to 2.
 * @returns {string} Abbreviated value, or "0" when the input is not numeric.
 */
export const formatNumberToBillions = (value, decimals = 2) => {
  const num = typeof value === "string" ? parseFloat(value) : Number(value);
  const precision = decimals === 0 ? 0 : 2;

  const formatValue = (rawValue) => {
    const formattedValue = rawValue.toFixed(precision);

    if (precision === 0) {
      return formattedValue;
    }

    // Drop trailing zeros so 1.50 -> 1.5 and 1.00 -> 1
    return formattedValue.replace(/\.0+$/, "").replace(/(\.\d*?[1-9])0+$/, "$1");
  };

  if (isNaN(num)) {
    return "0";
  }

  const absoluteNum = Math.abs(num);

  if (absoluteNum >= 1_000_000_000_000) {
    return formatValue(num / 1_000_000_000_000) + "Tr";
  }

  if (absoluteNum >= 1_000_000_000) {
    return formatValue(num / 1_000_000_000) + "Bn";
  }

  if (absoluteNum >= 1_000_000) {
    return formatValue(num / 1_000_000) + "M";
  }

  if (absoluteNum >= 1_000) {
    return formatValue(num / 1_000) + "K";
  }

  return formatValue(num);
};

/**
 * @param {string} countryCode Selected market code.
 * @returns {boolean} True when Vietnam, the only market using the app-aligned formatter.
 */
export const isVnCountryCode = (countryCode) =>
  String(countryCode ?? "").toLowerCase() === "vn";


/**
 * Checks whether a column contains a Pakistan sales actual or target value.
 *
 * @param {string} countryCode Selected market code.
 * @param {object} columnDefinition Grid column metadata.
 * @returns {boolean} True only for PK sales actual/target columns.
 */
export const isPkSalesValueColumn = (countryCode, columnDefinition = {}) => {
  if (String(countryCode ?? "").toLowerCase() !== "pk") {
    return false;
  }

  const columnText = `${columnDefinition.headerName || ""} ${columnDefinition.field || ""} ${columnDefinition.colId || ""}`
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ");
  return /\b(sales|gsv)\b/i.test(columnText) && /\b(actuals?|achieved|target)\b/i.test(columnText);
};


/**
 * Formats PK sales values with four decimals while retaining abbreviations.
 *
 * @param {string|number} value Raw sales value.
 * @returns {string} Value with four decimals and an optional magnitude suffix.
 */
export const formatPkSalesValue = (value) => {
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) {
    return String(value ?? "");
  }

  const absoluteValue = Math.abs(numericValue);
  const magnitudes = [
    [1_000_000_000_000, "Tr"],
    [1_000_000_000, "Bn"],
    [1_000_000, "M"],
    [1_000, "K"],
  ];
  const magnitude = magnitudes.find(([threshold]) => absoluteValue >= threshold);

  if (!magnitude) {
    return numericValue.toFixed(4);
  }

  return `${(numericValue / magnitude[0]).toFixed(4)}${magnitude[1]}`;
};

/**
 * Abbreviates a value using the app formatter for VN and the legacy library elsewhere.
 *
 * @param {string|number} value Raw value to abbreviate.
 * @param {string} countryCode Selected market code.
 * @returns {string} Abbreviated value.
 */
export const formatAbbreviatedNumber = (value, countryCode) => {
  if (isVnCountryCode(countryCode)) {
    return formatNumberToBillions(value);
  }

  const numberAbbreviator = new NumAbbr(["K", "M", "Bn", "Tr"]);
  return numberAbbreviator.abbreviate(value, 2);
};

export default formatNumberToBillions;
